import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";
import {
  buildLanguageSelectOptions,
  getLanguageCompactLabel,
  getLanguageFlag,
  getLanguageLabel,
  normalizeLanguageCode,
} from "@/lib/languages";
import { cn } from "@/lib/utils";

export default function LanguageSwitcher({
  value = "",
  onChange,
  countryCode = "",
  browserLanguages = [],
  shareLinkLanguageCode = "",
  preferredUiLanguageCode = "",
  uiOnly = true,
  className = "",
  compactOnMobile = false,
  fitContent = false,
}) {
  const { locale, setLocale, t } = useI18n();
  const resolvedValue = normalizeLanguageCode(value || locale);
  const options = buildLanguageSelectOptions({
    countryCode,
    browserLanguages,
    shareLinkLanguageCode,
    preferredUiLanguageCode: preferredUiLanguageCode || resolvedValue,
    uiOnly,
  });
  const selectedLanguage = options.find((language) => language.code === resolvedValue);
  const selectedLabel = selectedLanguage?.label || getLanguageLabel(resolvedValue);
  const compactLabel = `${getLanguageFlag(resolvedValue)} ${getLanguageCompactLabel(resolvedValue)}`.trim();

  return (
    <Select
      value={resolvedValue}
      onValueChange={(nextLanguageCode) => {
        setLocale(nextLanguageCode);
        onChange?.(nextLanguageCode);
      }}
    >
      <SelectTrigger
        aria-label={`${t("common.language")}: ${selectedLabel}`}
        className={cn(
          "h-9 rounded-full border-border/70 bg-background/95",
          fitContent ? "w-auto min-w-0 text-xs font-semibold tracking-normal" : "min-w-[9.5rem] text-sm",
          compactOnMobile && "max-[559px]:h-8 max-[559px]:px-2.5 max-[559px]:text-xs",
          className,
        )}
      >
        {compactOnMobile ? (
          <>
            <div className="max-[559px]:hidden">
              <SelectValue placeholder={t("common.language")} />
            </div>
            <div className="hidden items-center gap-1.5 font-semibold uppercase tracking-normal max-[559px]:inline-flex">
              {compactLabel}
            </div>
          </>
        ) : (
          <SelectValue placeholder={t("common.language")} />
        )}
      </SelectTrigger>
      <SelectContent align="end">
        {options.map((language) => (
          <SelectItem key={language.code} value={language.code}>
            {language.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
